/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState, useCallback, Fragment } from 'react'

import { useQuery } from '@apollo/client'
import classNames from 'classnames'
import InfiniteScroll from 'react-infinite-scroller'

import {
  CommonRefresh2Icon,
  ReviewOnlyPhotoFilterIcon,
  FeedBetaIcon,
  CommonQuestionIcon,
  CommonCloseIcon,
} from '@local/assets/icons'
import { extractByTypename } from '@local/utils'

import Advertisement, { AdPage, AdType } from '@web/components/Advertisement/Advertisement'
import { getADElementIdMap } from '@web/components/Advertisement/utils'
import Clickable from '@web/components/Clickable'
import Loading from '@web/components/Loading'
import NoContent from '@web/components/NoContent'
import { NoContentTypes } from '@web/components/NoContent/type'
import { gql } from '@web/gql'
import {
  PlaceOtherReviewList_ReviewGroupFragment,
  PlaceOtherReviewGroupsInput,
  ReviewGroupsMediaFilter,
  PlaceOtherReviewGroupsSort,
  PlaceOtherReviewGroupsQuery,
  PlaceOtherReviewList_ReviewGroupConnectionFragment,
} from '@web/gql/graphql'
import useIncreaseReviewGroupsView from '@web/hooks/useIncreaseReviewGroupsView'
import { isTypename } from '@web/libs/apollo/typeGuard'
import { RECOMMEND_CATEGORY_CODES } from '@web/libs/constants'
import nClicks from '@web/libs/nlog/click/codes'
import { generateRandomKey, truncateNumber } from '@web/libs/utils'

import styles from './index.module.scss'
import PlaceOtherReview from './PlaceOtherReview'

const determineRecommendCategory = (categoryIdPath?: string): boolean => {
  if (!categoryIdPath) return false
  return categoryIdPath.split('>').filter(code => RECOMMEND_CATEGORY_CODES.includes(code)).length > 0
}

interface Props {
  reviewGroup: PlaceOtherReviewList_ReviewGroupFragment
  isMine: boolean
}

const query = gql(`
  query PlaceOtherReviewGroups($input: PlaceOtherReviewGroupsInput!) {
    placeOtherReviewGroups(input: $input) {
      ... on ReviewGroupConnection {
        ...PlaceOtherReviewList_ReviewGroupConnection
      }
      ... on BaseError {
        message
      }
    }
  }
`)

const getWithoutMainReviewGroup = (
  reviewGroup: PlaceOtherReviewList_ReviewGroupFragment,
  data: PlaceOtherReviewGroupsQuery | undefined,
  mediaFilter: ReviewGroupsMediaFilter,
): PlaceOtherReviewList_ReviewGroupConnectionFragment | null => {
  const { ReviewGroupConnection } = extractByTypename(data?.placeOtherReviewGroups)
  if (!ReviewGroupConnection) return null

  const hasMainReviewGroup =
    mediaFilter === ReviewGroupsMediaFilter.WithMedia ? reviewGroup.mediaItems.length > 0 : true

  if (!hasMainReviewGroup) return ReviewGroupConnection

  return {
    ...ReviewGroupConnection,
    edges: ReviewGroupConnection.edges.filter(r => r.reviewGroup.id !== reviewGroup.id),
    pageInfo: {
      ...ReviewGroupConnection.pageInfo,
      totalCount: ReviewGroupConnection.pageInfo.totalCount - 1,
    },
  }
}

const PlaceOtherReviewList = ({ reviewGroup, isMine }: Props) => {
  const isRecommendCategory = determineRecommendCategory(reviewGroup.place?.categoryIdPath ?? undefined)

  const [onlyMedia, setOnlyMedia] = useState(false)
  const [sort, setSort] = useState<PlaceOtherReviewGroupsSort>(
    isRecommendCategory ? PlaceOtherReviewGroupsSort.RecommendScoreDesc : PlaceOtherReviewGroupsSort.VisitDateTimeDesc,
  )
  const [showSortGuideLayer, setShowSortGuideLayer] = useState(false)

  const toggleSortGuideLayer = useCallback(() => setShowSortGuideLayer(prev => !prev), [])
  const increaseReviewGroupsView = useIncreaseReviewGroupsView()

  const mediaFilter = onlyMedia ? ReviewGroupsMediaFilter.WithMedia : ReviewGroupsMediaFilter.All
  const contentInput: PlaceOtherReviewGroupsInput = useMemo(() => {
    return {
      placeId: reviewGroup.place?.id ?? '',
      excludeReviewGroupId: reviewGroup.id,
      mediaFilter,
      sort,
      size: 20,
    }
  }, [reviewGroup.id, onlyMedia, sort])

  const { data, loading, error, refetch, fetchMore } = useQuery(query, {
    fetchPolicy: 'cache-first',
    variables: { input: contentInput },
    onCompleted: data => {
      const connection = getWithoutMainReviewGroup(reviewGroup, data, mediaFilter)
      if (connection?.edges) {
        increaseReviewGroupsView(connection.edges.map(edge => edge.reviewGroup.id))
      }
    },
    skip: !reviewGroup.place?.id,
  })

  if (!reviewGroup.place?.id) return null

  const title = '이 장소의 다른 리뷰'
  if (error || isTypename(data?.placeOtherReviewGroups, 'UnknownError')) {
    return (
      <div className={styles.review_list_area}>
        <h2 className={styles.tit_area}>
          {title}
          <Clickable className={styles.btn_refresh} onClick={() => refetch()}>
            <span className="place_blind">새로고침</span>
            <CommonRefresh2Icon className={styles.icon} aria-hidden="true" />
          </Clickable>
        </h2>
      </div>
    )
  }

  const connection = getWithoutMainReviewGroup(reviewGroup, data, mediaFilter)
  if (!connection || loading) return <Loading />

  const { edges, pageInfo } = connection

  const showableReviewGroups = edges.filter(({ reviewGroup }) => !!reviewGroup.author)
  const adElementIdMap = getADElementIdMap(showableReviewGroups.length, 'REVIEW_PLACE_END')

  const isShowableReviewExist = showableReviewGroups.length > 0

  const loadMore = () => {
    fetchMore({
      variables: {
        input: {
          ...contentInput,
          after: pageInfo.endCursor,
        },
      },
    })
  }

  return (
    <>
      <h2 className={styles.tit_area}>
        <div className={styles.tit_inner}>
          {title}
          {pageInfo.totalCount > -1 && (
            <span className="place_section_count">{truncateNumber(pageInfo.totalCount)}</span>
          )}
        </div>
        <Clickable
          className={styles.btn_only_photo_review}
          role="checkbox"
          aria-checked={onlyMedia}
          onClick={() => setOnlyMedia(!onlyMedia)}
        >
          <ReviewOnlyPhotoFilterIcon className={styles.icon} aria-hidden="true" />
          사진/영상 리뷰만
        </Clickable>
      </h2>
      {isRecommendCategory && (
        <div className={styles.sort_area} role="listbox">
          <div className={styles.inner}>
            {isShowableReviewExist && (
              <span className={styles.sort_wrapper}>
                <Clickable
                  className={styles.sort}
                  role="option"
                  aria-selected={sort === PlaceOtherReviewGroupsSort.RecommendScoreDesc}
                  onClick={() => setSort(PlaceOtherReviewGroupsSort.RecommendScoreDesc)}
                  nClickCode={nClicks.reviewEndOthers.SORTBEST}
                >
                  추천순
                  <span className={styles.label_beta}>
                    <FeedBetaIcon className={styles.icon} aria-hidden="true" />
                    <span className="place_blind">베타</span>
                  </span>
                </Clickable>
              </span>
            )}
            <span className={styles.sort_wrapper}>
              <Clickable
                className={styles.sort}
                role="option"
                aria-selected={sort === PlaceOtherReviewGroupsSort.VisitDateTimeDesc || !isShowableReviewExist}
                onClick={() => setSort(PlaceOtherReviewGroupsSort.VisitDateTimeDesc)}
                nClickCode={nClicks.reviewEndOthers.SORTRECENT}
              >
                최신순
              </Clickable>
            </span>
          </div>
          <Clickable
            className={styles.btn_guide}
            aria-haspopup="true"
            aria-expanded={showSortGuideLayer}
            onClick={toggleSortGuideLayer}
            nClickCode={nClicks.reviewEndOthers.SORTINFO}
          >
            <CommonQuestionIcon className={styles.icon} aria-hidden="true" />
            <span className="place_blind">정렬 안내</span>
          </Clickable>
          {showSortGuideLayer && (
            <div className={styles.ly_guide}>
              <div className={styles.txt_area}>
                <div className={styles.item}>
                  <strong>추천순</strong>
                  리뷰의 텍스트와 사진, 작성자의 리뷰 및 방문 활동, 방문 시점, 리뷰 표현을 고려하여 리뷰를 추천합니다.
                </div>
                <div className={styles.item}>
                  <strong>최신순</strong>
                  최근 방문한 순서로 리뷰를 정렬합니다.
                </div>
              </div>
              <Clickable
                className={styles.btn_close}
                onClick={toggleSortGuideLayer}
                nClickCode={nClicks.reviewEndOthers.CLOSESORTINFO}
              >
                <CommonCloseIcon className={styles.icon} aria-hidden="true" />
                <span className="place_blind">안내 레이어 닫기</span>
              </Clickable>
            </div>
          )}
        </div>
      )}
      <InfiniteScroll
        element="ul"
        className={styles.review_list}
        pageStart={1}
        loadMore={loadMore}
        hasMore={pageInfo.totalCount > edges.length && !!pageInfo.endCursor}
        loader={<Loading key={generateRandomKey()} />}
      >
        {isShowableReviewExist ? (
          showableReviewGroups.map(({ reviewGroup }, index) => {
            const showAD = !!adElementIdMap[index]
            return (
              <Fragment key={reviewGroup.id}>
                <PlaceOtherReview reviewGroup={reviewGroup} />
                {showAD && (
                  <li className={classNames(styles.list_item, styles.type_ad)}>
                    <Advertisement
                      adSlotElementId={adElementIdMap[index]}
                      adSlotElementIds={Object.values(adElementIdMap)}
                      type={AdType.FEED_REVIEW_END}
                      page={isMine ? AdPage.REVIEW_END_MINE : AdPage.REVIEW_END_OTHERS}
                      userIdno={reviewGroup.author?.idno}
                    />
                  </li>
                )}
              </Fragment>
            )
          })
        ) : (
          <NoContent type={onlyMedia ? NoContentTypes.REVIEW_NO_PHOTO : NoContentTypes.REVIEW_NO_RESULT} />
        )}
      </InfiniteScroll>
    </>
  )
}

PlaceOtherReviewList.fragments = {
  reviewGroup: gql(`
    fragment PlaceOtherReviewList_ReviewGroup on ReviewGroup {
      id
      place {
        id
        categoryIdPath
      }
      mediaItems {
        id
      }
    }
  `),
  reviewGroupConnection: gql(`
    fragment PlaceOtherReviewList_ReviewGroupConnection on ReviewGroupConnection {
      edges {
        cursor
        reviewGroup {
          ...PlaceOtherReview_ReviewGroup
        }
      }
      pageInfo {
        totalCount
        endCursor
      }
    }
  `),
}

export default PlaceOtherReviewList
